/**
 * 智能体管理页面
 *
 * 显示系统智能体和用户自定义智能体列表
 * 支持创建、编辑、删除自定义智能体
 */

import React, { useEffect, useCallback, useState } from 'react';
import { AuthorCenterLayout } from '../components/layout/AuthorCenterLayout';
import { useAgentStore } from '../store/agentStore';
import { AgentConfigModal } from '../components/agent/AgentConfigModal';
import type { Agent } from '../types/agent';
import { SkillsPage } from './SkillsPage';

// 图标组件
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const SystemBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
    系统
  </span>
);

const ModeBadge: React.FC<{ mode: string }> = ({ mode }) => {
  const modeConfig: Record<string, { bg: string; text: string; label: string }> = {
    primary: { bg: 'bg-blue-100', text: 'text-blue-700', label: '主智能体' },
    subagent: { bg: 'bg-orange-100', text: 'text-orange-700', label: '子智能体' },
    all: { bg: 'bg-purple-100', text: 'text-purple-700', label: '通用' },
  };

  const config = modeConfig[mode] || modeConfig.primary;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

/**
 * 智能体卡片组件
 */
interface AgentCardProps {
  agent: Agent;
  onEdit?: (agent: Agent) => void;
  onDelete?: (agent: Agent) => void;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, onEdit, onDelete }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-medium text-gray-900">{agent.name}</h3>
            {agent.is_system && <SystemBadge />}
            <ModeBadge mode={agent.mode || 'primary'} />
          </div>
          <p className="mt-1 text-sm text-gray-800 line-clamp-2">
            {agent.prompt.slice(0, 100)}...
          </p>
          <div className="mt-2 flex items-center gap-4 text-sm text-gray-800">
            <span>温度: {agent.temperature}</span>
            <span>工具: {agent.enabled_tools.length}个</span>
            {agent.enabled_skills && agent.enabled_skills.length > 0 && (
              <span>技能: {agent.enabled_skills.length}个</span>
            )}
          </div>
        </div>
        {!agent.is_system && (
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => onEdit?.(agent)}
              className="p-1.5 text-gray-800 hover:text-gray-900 hover:bg-gray-100 rounded"
              title="编辑"
            >
              <EditIcon />
            </button>
            <button
              onClick={() => onDelete?.(agent)}
              className="p-1.5 text-gray-800 hover:text-red-600 hover:bg-red-50 rounded"
              title="删除"
            >
              <TrashIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 智能体管理页面组件
 */
export const AgentsPage: React.FC = () => {
  // 标签页状态
  const [activeTab, setActiveTab] = useState<'agents' | 'skills'>('agents');

  // 从 store 获取状态和方法
  const {
    loading,
    error,
    fetchAgents,
    deleteAgent,
    clearError,
    getSystemAgents,
    getUserAgents,
  } = useAgentStore();

  // 模态框状态
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [deletingAgent, setDeletingAgent] = useState<Agent | null>(null);

  // 加载智能体列表
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // 获取分类后的智能体
  const systemAgents = getSystemAgents();
  const userAgents = getUserAgents();

  // 处理创建智能体
  const handleCreate = useCallback(() => {
    setEditingAgent(null);
    setShowModal(true);
  }, []);

  // 处理编辑智能体
  const handleEdit = useCallback((agent: Agent) => {
    setEditingAgent(agent);
    setShowModal(true);
  }, []);

  // 处理删除确认
  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingAgent) return;

    try {
      await deleteAgent(deletingAgent.id);
      setDeletingAgent(null);
    } catch (e) {
      console.error('删除智能体失败:', e);
    }
  }, [deletingAgent, deleteAgent]);

  // 处理模态框关闭
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setEditingAgent(null);
  }, []);

  // 处理模态框保存成功
  const handleModalSuccess = useCallback(() => {
    setShowModal(false);
    setEditingAgent(null);
    fetchAgents();
  }, [fetchAgents]);

  return (
    <AuthorCenterLayout>
      <div className="min-h-screen bg-white">
        {/* 标签页切换栏 */}
        <div className="border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex gap-6">
              <button
                onClick={() => setActiveTab('agents')}
                className={`py-4 px-1 border-b-2 font-medium transition-colors ${
                  activeTab === 'agents'
                    ? 'border-paper-500 text-paper-700'
                    : 'border-transparent text-gray-800 hover:text-paper-700 hover:border-paper-300'
                }`}
              >
                智能体管理
              </button>
              <button
                onClick={() => setActiveTab('skills')}
                className={`py-4 px-1 border-b-2 font-medium transition-colors ${
                  activeTab === 'skills'
                    ? 'border-paper-500 text-paper-700'
                    : 'border-transparent text-gray-800 hover:text-paper-700 hover:border-paper-300'
                }`}
              >
                技能管理
              </button>
            </div>
          </div>
        </div>

        {/* 条件渲染内容 */}
        {activeTab === 'skills' ? (
          <SkillsPage />
        ) : (
          /* 主内容区 */
          <main className="max-w-5xl mx-auto px-6 py-8">
          {/* 标题和创建按钮 */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">智能体管理</h2>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 bg-ink-800 hover:bg-ink-700 text-white px-4 py-2 rounded-md font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150"
            >
              <PlusIcon />
              新建智能体
            </button>
          </div>

          {/* 错误状态 */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
              {error}
              <button onClick={clearError} className="ml-4 underline">
                关闭
              </button>
            </div>
          )}

          {/* 加载状态 */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-paper-500"></div>
              <p className="mt-4 text-gray-800">加载中...</p>
            </div>
          )}

          {/* 系统智能体区域 */}
          {!loading && systemAgents.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">系统智能体</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {systemAgents.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} />
                ))}
              </div>
            </div>
          )}

          {/* 用户自定义智能体区域 */}
          {!loading && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">自定义智能体</h3>
              {userAgents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userAgents.map((agent) => (
                    <AgentCard
                      key={agent.id}
                      agent={agent}
                      onEdit={handleEdit}
                      onDelete={setDeletingAgent}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-gray-800 mb-4">还没有自定义智能体</p>
                  <button
                    onClick={handleCreate}
                    className="text-paper-700 hover:text-paper-800 font-medium"
                  >
                    创建第一个智能体
                  </button>
                </div>
              )}
            </div>
          )}
          </main>
        )}

        {/* 模态框和对话框（只在智能体标签页显示） */}
        {activeTab === 'agents' && (
          <>
            {/* 配置模态框 */}
            <AgentConfigModal
              isOpen={showModal}
              onClose={handleModalClose}
              agent={editingAgent}
              onSuccess={handleModalSuccess}
            />

            {/* 删除确认对话框 */}
            {deletingAgent && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">确认删除</h3>
                  <p className="text-gray-800 mb-6">
                    确定要删除智能体「{deletingAgent.name}」吗？此操作不可撤销。
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setDeletingAgent(null)}
                      className="px-4 py-2 text-gray-800 hover:bg-gray-100 rounded-md"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleDeleteConfirm}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AuthorCenterLayout>
  );
};

export default AgentsPage;
